import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCloseButton,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { RegisterOptions, useForm } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { SavedView } from '../../../../gql/graphql';

type SaveViewModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isSavingView: boolean;
  savedViews: SavedView[];
  nameRegisterOptions: RegisterOptions;
  onSaveNewView: (
    name: string,
    includeCameraPosition: boolean,
    setAsHomeView: boolean
  ) => Promise<void>;
  onOverrideViewConfirm: (
    name: string,
    includeCameraPosition: boolean,
    setAsHomeView: boolean
  ) => Promise<void>;
};

type SavedViewFormState = {
  name: string;
  includeCameraPosition: boolean;
  setAsHomeView: boolean;
  confirmOverwrite: boolean;
};

export default function SaveViewModal({
  isOpen: isModalOpen,
  savedViews,
  nameRegisterOptions,
  onClose,
  onSaveNewView,
  onOverrideViewConfirm,
}: SaveViewModalProps): React.ReactElement {
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const {
    isOpen: isConfirmModalOpen,
    onOpen: openConfirmModal,
    onClose: closeConfirmModal,
  } = useDisclosure();
  const cancelConfirmRef = useRef<HTMLButtonElement>(null);

  const handleCancelOverride = () => {
    closeConfirmModal();
    invariant(nameInputRef.current, 'Name input ref is not defined');
    nameInputRef.current.focus();
  };

  const {
    register,
    handleSubmit,
    formState: { isValid: isFormValid, errors: formErrors, touchedFields },
    reset: resetForm,
    watch,
  } = useForm<SavedViewFormState>();
  const [currentViewInputName] = watch(['name']);
  const [currentViewIncludeCameraPosition] = watch(['includeCameraPosition']);

  useEffect(() => {
    if (savedViews) {
      resetForm({
        name: `View ${(savedViews.length ?? 0) + 1}`,
        includeCameraPosition: true,
        setAsHomeView: false,
      });
    }
  }, [savedViews, resetForm]);

  const onCloseInternal = () => {
    resetForm({
      name: `View ${(savedViews.length ?? 0) + 1}`,
      includeCameraPosition: true,
      setAsHomeView: false,
    });
    onClose();
  };

  const [isSavingView, setIsSavingView] = useState(false);

  const handleSaveNewViewSubmit = handleSubmit(
    async ({ name, includeCameraPosition, setAsHomeView }) => {
      // Prompt to confirm overwrite
      if (savedViews?.some((view) => view.name === name)) {
        openConfirmModal();
        return;
      }

      setIsSavingView(true);
      await onSaveNewView(name, includeCameraPosition, setAsHomeView);
      setIsSavingView(false);
    }
  );

  const handleOverrideViewConfirm = handleSubmit(
    async ({ name, includeCameraPosition, setAsHomeView }) => {
      await onOverrideViewConfirm(name, includeCameraPosition, setAsHomeView);
    }
  );

  const { ref: nameInputFormRef, ...nameInputProps } = register(
    'name',
    nameRegisterOptions
  );

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        onClose={onCloseInternal}
        initialFocusRef={nameInputRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Save current view</ModalHeader>
          <ModalCloseButton />
          <Divider orientation="horizontal" />
          <ModalBody>
            <form onSubmit={handleSaveNewViewSubmit} id="save-view-form">
              <FormControl
                isRequired
                isInvalid={formErrors.name && touchedFields.name}
              >
                <FormLabel>Name</FormLabel>
                <Input
                  {...nameInputProps}
                  ref={(e) => {
                    nameInputRef.current = e;
                    nameInputFormRef(e);
                  }}
                  placeholder="Name"
                />
                <FormErrorMessage>{formErrors.name?.message}</FormErrorMessage>
              </FormControl>
              <CheckboxGroup>
                <VStack align="start" mt={2}>
                  <Checkbox
                    {...register('includeCameraPosition')}
                    defaultChecked={true}
                  >
                    Include camera position
                  </Checkbox>
                  <Checkbox
                    isDisabled={!currentViewIncludeCameraPosition}
                    defaultChecked={false}
                    {...register('setAsHomeView')}
                  >
                    Set as home view
                  </Checkbox>
                </VStack>
              </CheckboxGroup>
            </form>
          </ModalBody>
          <Divider orientation="horizontal" mt={1} />
          <ModalFooter gap={2}>
            <Button
              type="submit"
              variant="solid"
              colorScheme="brand"
              form="save-view-form"
              isDisabled={!isFormValid}
              isLoading={isSavingView}
              loadingText="Saving"
            >
              Save
            </Button>
            <Button onClick={onClose} colorScheme="gray">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={isConfirmModalOpen}
        onClose={handleCancelOverride}
        leastDestructiveRef={cancelConfirmRef}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          <AlertDialogHeader>Overwrite view</AlertDialogHeader>
          <AlertDialogCloseButton />
          <Divider mb={2} orientation="horizontal" />
          <AlertDialogBody>
            Are you sure you want to overwrite the
            <Badge mx={1}>{currentViewInputName}</Badge> with the current view?
          </AlertDialogBody>
          <Divider mt={2} orientation="horizontal" />
          <AlertDialogFooter gap={2}>
            <Button
              ref={cancelConfirmRef}
              colorScheme="gray"
              onClick={handleCancelOverride}
            >
              Cancel
            </Button>
            <Button
              colorScheme="red"
              onClick={() => {
                closeConfirmModal();
                handleOverrideViewConfirm();
              }}
            >
              Confirm
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
